import { IAuthorizedUserValidity } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { IAuthorizedUser, IPersonalInfo, IPersonalInfoErrors, TPersonalInfoError } from '../../providers/AcquisitionProvider/models';
import {
  TAcquisitionState,
  TInputField,
} from '../../providers/ModernAcquisitionProvider/types';

export type TLegacyPersonalInfoFromFieldValue = { value: string; valid: boolean };
export type TPersonalInfoEntries = [ string, TLegacyPersonalInfoFromFieldValue ][];
const legacyReadManager = (
  personalInfoForm: IPersonalInfo,
  personalInfoErrors: IPersonalInfoErrors,
  authorizedUserInfo: IAuthorizedUser,
  authorizedUserValidity: IAuthorizedUserValidity  
) => {
  const normalize = () => {
    const normalizedState: TAcquisitionState = {};

    const adaptToNormalized = ({ value, valid }: TLegacyPersonalInfoFromFieldValue, error: TPersonalInfoError): TInputField => {
      const normalizedObject: TInputField = { value: value, state: 'neutral', errorType: 'None' };

      switch (error) {
        case 'RequiredField': normalizedObject.errorType = 'requiredField'; break;
        case 'NotValid': normalizedObject.errorType = 'invalidInput'; break;
        case 'ThirdParty': normalizedObject.errorType = 'invalidInput'; break;
        case 'NotEligible': normalizedObject.errorType = 'notEligible'; break;
        case 'None':
        default: normalizedObject.errorType = 'None'; break;
      }

      if (!value && valid) {
        // Legacy field is in a neutral state if 'value' is empty and 'valid' is true
        normalizedObject.state = 'neutral';
      } else if (!!value && valid) {
        // Legacy field is in success state if 'value' is not empty and 'valid' is true
        normalizedObject.state = 'success';
      } else if (!valid) {
        normalizedObject.state = 'error';
      }

      return normalizedObject;
    };

    //Applicant
    (Object.entries(personalInfoForm) as TPersonalInfoEntries)
      .forEach(([legacyKey, legacyField]) => {
        switch (legacyKey) {
          case 'firstName': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, personalInfoErrors.FirstName); 
            break;
          }
          case 'lastName': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, personalInfoErrors.LastName); 
            break;
          }
          case 'phoneNumber': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, personalInfoErrors.PrimaryPhoneNumber); 
            break;
          }
          case 'ssn': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, personalInfoErrors.Ssn); 
            break;
          }
          case 'suffix': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, personalInfoErrors.NameSuffix); 
            break;
          }
          case 'monthlyIncome': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, personalInfoErrors.MonthlyIncome); 
            break;
          }
          case 'email': {
            normalizedState['emailAddress'] = adaptToNormalized(legacyField, personalInfoErrors.EmailAddress); 
            break;
          }
          case 'middleInitial': {
            normalizedState[legacyKey] = adaptToNormalized(legacyField, 'None'); 
            break;
          }
          case 'dob':
            // eslint-disable-next-line no-case-declarations
            const dobArr = legacyField['value'].split('/');
            normalizedState['dateOfBirthMonth'] = adaptToNormalized({
              value: dobArr?.[0] ?? '',
              valid: legacyField.valid,
            }, personalInfoErrors.DateOfBirth);
            normalizedState['dateOfBirthDay'] = adaptToNormalized({
              value: dobArr[1] ?? '',
              valid: legacyField.valid,
            }, personalInfoErrors.DateOfBirth);
            normalizedState['dateOfBirthYear'] = adaptToNormalized({
              value: dobArr[2] ?? '',
              valid: legacyField.valid,
            }, personalInfoErrors.DateOfBirth);
            break;
          case 'mailingAddress':
            normalizedState['mailingAddressLineOne'] =
              adaptToNormalized(legacyField, personalInfoErrors.MailingAddressLine1);
            break;
          case 'mailingAptUnit':
            normalizedState['mailingAddressLineTwo'] =
              adaptToNormalized(legacyField, personalInfoErrors.MailingAddressLine2);
            break;
          case 'mailingCity':
            normalizedState['mailingAddressCity'] =
              adaptToNormalized(legacyField, personalInfoErrors.MailingCity);
            break;
          case 'mailingState':
            normalizedState['mailingAddressState'] =
              adaptToNormalized(legacyField, personalInfoErrors.MailingState);
            break;
          case 'mailingZip':
            normalizedState['mailingAddressZipCode'] =
              adaptToNormalized(legacyField, personalInfoErrors.MailingZipCode);
            break;
          case 'homeAddress':
            normalizedState['homeAddressLineOne'] =
              adaptToNormalized(legacyField, personalInfoErrors.HomeAddressLine1);
            break;
          case 'homeAptUnit':
            normalizedState['homeAddressLineTwo'] =
              adaptToNormalized(legacyField, personalInfoErrors.HomeAddressLine2);
            break;
          case 'homeCity':
            normalizedState['homeAddressCity'] = adaptToNormalized(legacyField, personalInfoErrors.HomeCity);
            break;
          case 'homeState':
            normalizedState['homeAddressState'] = adaptToNormalized(legacyField, personalInfoErrors.HomeState);
            break;
          case 'homeZip':
            normalizedState['homeAddressZipCode'] =
              adaptToNormalized(legacyField, personalInfoErrors.HomeZipCode);
            break;
          default:
            break;
        }
    });

    // Legacy does not reliably instantiate any authorizedUserInfo properties
    // State is derived from validity object since the info object does not have 'valid' property
    [
      'AuthorizedUserFirstName',
      'AuthorizedUserLastName',
      'AuthorizedUserMiddleInitial',
      'AuthorizedUserDOBMonth',
      'AuthorizedUserDOBDay',
      'AuthorizedUserDOBYear',
      'IsAuthorizedUserSpouse',
  ].forEach((legacyKey) => {
      switch (legacyKey) {
        case 'AuthorizedUserFirstName':
          normalizedState['authorizedUserFirstName'] = {
            value: authorizedUserInfo?.[legacyKey] ?? '',
            state: authorizedUserValidity['authorizedUserFirstName'] ? 'success' : 'error',
            errorType: authorizedUserValidity['authorizedUserFirstName'] ? 'None' : 'requiredField',
          };
          break;
        case 'AuthorizedUserLastName':
          normalizedState['authorizedUserLastName'] = {
            value: authorizedUserInfo?.[legacyKey] ?? '',
            state: authorizedUserValidity['authorizedUserLastName'] ? 'success' : 'error',
            errorType: authorizedUserValidity['authorizedUserLastName'] ? 'None' : 'requiredField',
          };
          break;
        case 'AuthorizedUserMiddleInitial':
          const middleInitialValue = authorizedUserInfo?.[legacyKey] ?? '';
          normalizedState['authorizedUserMiddleInitial'] = {
            value: middleInitialValue,
            state: middleInitialValue === '' ? 'neutral' : 'success',
            errorType: 'None', // optional
          };
          break;
        case 'AuthorizedUserDOBMonth':
          normalizedState['authorizedUserDateOfBirthMonth'] = {
            value: authorizedUserInfo?.[legacyKey] ?? '',
            state: authorizedUserValidity['authorizedUserDob'] ? 'success' : 'error',
            errorType: authorizedUserValidity['authorizedUserDob'] ? 'None' : 'requiredField',
          };
          break;
        case 'AuthorizedUserDOBDay':
          normalizedState['authorizedUserDateOfBirthDay'] = {
            value: authorizedUserInfo?.[legacyKey] ?? '',
            state: authorizedUserValidity['authorizedUserDob'] ? 'success' : 'error',
            errorType: authorizedUserValidity['authorizedUserDob'] ? 'None' : 'requiredField',
          };
          break;
        case 'AuthorizedUserDOBYear':
          normalizedState['authorizedUserDateOfBirthYear'] = {
            value: authorizedUserInfo?.[legacyKey] ?? '',
            state: authorizedUserValidity['authorizedUserDob'] ? 'success' : 'error',
            errorType: authorizedUserValidity['authorizedUserDob'] ? 'None' : 'requiredField',
          };
          break;
        case 'IsAuthorizedUserSpouse':
          normalizedState['authorizedUserSpouse'] = {
            value: authorizedUserInfo?.[legacyKey] ?? null,
            state: authorizedUserValidity['authorizedUserSpouse'] ? 'neutral' : 'error',
            errorType: authorizedUserValidity['authorizedUserSpouse'] ? 'None' : 'invalidInput',
          };
          break;
        default:
          break;
      }
    });
    
    return { ...normalizedState };
  };

  return {
    normalize,
  };
};

export default legacyReadManager;
